
import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Shield, MessageSquare, Users, FileText, Cookie, Scale, Accessibility, BadgeIndianRupee } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface PolicyMenuItem {
  label: string;
  path: string;
  icon: React.ElementType;
  description?: string;
}

interface PolicyLayoutProps {
  children: ReactNode;
}

const policyMenuItems: PolicyMenuItem[] = [
  { 
    label: 'About Us', 
    path: '/about-us', 
    icon: Users,
    description: 'Learn about our company'
  },
  { 
    label: 'Contact Us', 
    path: '/contact-us', 
    icon: MessageSquare,
    description: 'Get in touch with us'
  },
  { 
    label: 'Pricing', 
    path: '/pricing', 
    icon: BadgeIndianRupee,
    description: 'Our subscription plans'
  },
  { 
    label: 'Privacy Policy', 
    path: '/privacy-policy', 
    icon: Shield,
    description: 'How we protect your data'
  },
  { 
    label: 'Terms & Conditions', 
    path: '/terms-and-conditions', 
    icon: Scale,
    description: 'Agreement terms'
  },
  { 
    label: 'Cookie Policy', 
    path: '/cookie-policy', 
    icon: Cookie,
    description: 'How we use cookies'
  },
  { 
    label: 'Accessibility', 
    path: '/accessibility-statement', 
    icon: Accessibility,
    description: 'Our commitment to accessibility'
  },
  { 
    label: 'Cancellation Policy', 
    path: '/cancellation-policy', 
    icon: BadgeIndianRupee,
    description: 'Refunds and cancellations'
  },
  { 
    label: 'GDPR Compliance', 
    path: '/gdpr-compliance', 
    icon: FileText,
    description: 'Data protection information'
  },
];

const PolicyLayout: React.FC<PolicyLayoutProps> = ({ children }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  return (
    <div className="container px-4 py-8 mx-auto">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Policy Navigation Sidebar */}
        <motion.aside 
          className="lg:w-72 shrink-0"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="sticky top-20 bg-gradient-to-br from-card to-secondary/5 border border-border/40 p-4 rounded-xl shadow-sm">
            <h2 className="text-xl font-semibold mb-4 text-primary">Information</h2>
            <ScrollArea className="h-[calc(100vh-220px)] pr-3">
              <div className="space-y-1">
                {policyMenuItems.map((item) => (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant={currentPath === item.path ? "secondary" : "ghost"}
                      className={cn(
                        "w-full justify-start text-left font-normal mb-1",
                        currentPath === item.path 
                          ? "bg-secondary/20" 
                          : "hover:bg-secondary/10"
                      )}
                    >
                      <item.icon className={cn(
                        "mr-2 h-4 w-4",
                        currentPath === item.path ? "text-primary" : "text-muted-foreground"
                      )} />
                      <span className={currentPath === item.path ? "text-primary" : "text-foreground"}>
                        {item.label}
                      </span>
                    </Button>
                  </Link>
                ))}
              </div>
            </ScrollArea>
            
            <div className="mt-6 pt-4 border-t border-border/30">
              <p className="text-xs text-muted-foreground">
                If you have any questions about our policies, please <Link to="/contact-us" className="text-primary hover:underline">contact us</Link>.
              </p>
            </div>
          </div>
        </motion.aside>

        {/* Main Content */}
        <motion.main 
          className="flex-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {children}
          
          {/* Back to top button */}
          <motion.button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 right-6 p-2 rounded-full bg-primary/80 text-white hover:bg-primary transition-colors shadow-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            whileHover={{ scale: 1.1 }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 19V5m0 0l-7 7m7-7l7 7" />
            </svg>
          </motion.button>
        </motion.main>
      </div>
    </div>
  );
};

export default PolicyLayout;
